/**
 * now-inbox → Lanes tab slots (components/lanes/slots.tsx): the real decision view in the tab's
 * inspector (replacing the read-only detail) and an asks panel built from the Inbox's own rows,
 * so the tab and the Inbox always show the same data. Registered once at module load.
 */
import { StatusIcon } from '@viko/transcript-ui'
import { Sparkles } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { splitOutdated } from '../../lib/lanes-layout.js'
import { blockedReason, isBlockedRow, needsYouCount } from '../../lib/needs-you.js'
import { groupInboxRows, type InboxRow } from '../../lib/now-inbox.js'
import { inboxRowEntity } from '../../lib/row-actions.js'
import { isTyping } from '../../lib/shortcut.js'
import { ItemDetailFor } from '../inbox/InboxInspector.js'
import { useInboxRows } from '../inbox/useInboxRows.js'
import { AsksHead } from '../lanes/asks-head.js'
import {
  type AsksPanelSlotProps,
  type DecisionInspectorSlotProps,
  provideLanesSlots,
} from '../lanes/slots.js'
import { RowActionBar, TRIAGE_KEYS, useRowActions } from '../RowActions.js'
import { Ago, errorText } from '../ui.js'
import { BatchView } from './BatchView.js'
import {
  DecisionPanel,
  parseDecisionKey,
  useDecisionActions,
  useDecisionModel,
} from './DecisionPanel.js'
import '../inbox/triage.css'

function LanesDecision({ inspectKey, item, onInspect, fallback }: DecisionInspectorSlotProps) {
  const { kind, id } = parseDecisionKey(inspectKey)
  const built = useInboxRows()
  const model = useDecisionModel(inspectKey, item)
  if (kind === 'batch') {
    const rows = built.rows.filter(
      (r) => r.kind === 'decision' && (r.workstreamId ?? r.view?.runId) === id,
    )
    return rows.length ? (
      <BatchView
        title={`${rows.length} decisions${rows[0]?.lane ? ` in ${rows[0].lane}` : ''}`}
        keys={rows.map((r) => r.key)}
      />
    ) : (
      <>{fallback}</>
    )
  }
  // inbox-detail: an ask that is not a question (a blocker, a failed landing, an alert, a card)
  // opens its own detail pane, never the decision inspector.
  const it = kind === 'ask' ? (model.item ?? item) : undefined
  if (it && it.type !== 'question') {
    const row = built.rows.find((r) => r.item === it || r.item?.id === it.id)
    return (
      <div className="lk-decision">
        <ItemDetailFor row={row ?? { title: it.title }} item={it} />
      </div>
    )
  }
  if (!model.view) return <>{fallback}</>
  return (
    <div className="lk-decision">
      <DecisionPanel
        inspectKey={inspectKey}
        {...(item ? { item } : {})}
        keys
        onOpenTask={(taskId) => onInspect(`task:${taskId}`)}
      />
    </div>
  )
}

function ProjectAsks({
  projectId,
  askIds,
  onInspect,
  fallback,
  inspectKey,
  outside,
}: AsksPanelSlotProps) {
  const built = useInboxRows()
  const act = useDecisionActions()
  const rows = useMemo(
    () => built.rows.filter((r) => r.projectId === projectId),
    [built.rows, projectId],
  )
  // Asks that may be outdated (their machine or run moved on) fold under one line.
  const { current, outdated } = splitOutdated(rows)
  // core-loop: the top ask with quick answers is highlighted and `1`-`9` answers it, as on Home.
  // An answered ask stays out of the way until the list refreshes.
  const [answered, setAnswered] = useState<ReadonlySet<string>>(new Set())
  const target = current.find((r) => r.view && r.quick?.length && !answered.has(r.key))
  const g = useRef(0)
  const quickRef = useRef<(r: InboxRow, optionId: string) => Promise<void>>(async () => {})
  const latest = useRef<{ target: InboxRow | undefined; open: boolean }>({
    target,
    open: !!inspectKey,
  })
  latest.current = { target, open: !!inspectKey }
  const batchRef = useRef<() => void>(() => {})
  // biome-ignore lint/correctness/useExhaustiveDependencies: reads the latest ask through a ref
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      if (
        e.shiftKey &&
        e.key === 'D' &&
        !document.querySelector('[role="dialog"][aria-modal="true"]')
      ) {
        e.preventDefault()
        batchRef.current()
        return
      }
      if (e.shiftKey) return
      // `g 2` (go to tab 2) is a chord: its digit is not an answer.
      if (e.key === 'g') g.current = Date.now()
      if (!/^[1-9]$/.test(e.key) || Date.now() - g.current < 1200) return
      const { target: t, open } = latest.current
      // An open ask owns the digits (its own options); so does any dialog.
      if (open || !t || document.querySelector('[role="dialog"][aria-modal="true"]')) return
      const q = t.quick?.find((x) => x.key === Number(e.key))
      if (!q) return
      e.preventDefault()
      e.stopImmediatePropagation()
      setAnswered((a) => new Set([...a, t.key]))
      void quickRef.current(t, q.id)
    }
    addEventListener('keydown', onKey, true)
    return () => removeEventListener('keydown', onKey, true)
  }, [])
  // The tab's model counted asks this list cannot show (fixtures, another data source): keep
  // the tab's own panel rather than disagree with its badge.
  const known = new Set(rows.map((r) => r.item?.id).filter(Boolean))
  if (askIds.some((a) => !known.has(a))) return <>{fallback}</>
  const assumptions = built.assumptions.filter(
    (d) => (built.decisions.store?.projectOf(d) ?? d.projectId) === projectId,
  )
  if (!rows.length && !assumptions.length) return <>{fallback}</>
  const blocking = needsYouCount(built.rows, projectId) // one count source: lib/needs-you.ts
  const session = groupInboxRows(rows, 'leverage', [])
    .flatMap((g) => g.entries)
    .find((e) => e.type === 'session')
  batchRef.current = () => {
    if (session?.type === 'session') onInspect(`batch:${session.id}`)
  }
  const quick = async (r: InboxRow, optionId: string) => {
    if (!r.view) return
    try {
      if (await act.answer(r.view, { mode: 'pick', optionId }))
        act.toast(`Answered: ${r.quick?.find((q) => q.id === optionId)?.label ?? 'picked'}`)
      else setAnswered((a) => new Set([...a].filter((k) => k !== r.key)))
    } catch (e) {
      setAnswered((a) => new Set([...a].filter((k) => k !== r.key)))
      act.toast(errorText(e), 'error')
    }
  }
  quickRef.current = quick
  const row = (r: InboxRow) => (
    <ProjectAskRow
      key={r.key}
      row={r}
      focused={target?.key === r.key}
      current={!!inspectKey && inspectKey === keyOf(r)}
      onOpen={() => onInspect(keyOf(r))}
    />
  )
  return (
    <section className="lk-asks" aria-labelledby="lk-asks-h" data-asks-source="inbox">
      <AsksHead
        projectId={projectId}
        blocking={blocking}
        rows={current.length}
        action={
          session?.type === 'session' ? (
            // app-clarity: a quiet `D` hint in the section head (it used to be a wide button that
            // collided with the Lanes head at 1440).
            <button
              type="button"
              className="lk-dhint"
              aria-keyshortcuts="Shift+D"
              aria-label={`Decide ${session.rows.length} one by one`}
              title={`${session.rows.length} in ${session.rows[0]?.lane ?? 'one lane'}`}
              onClick={() => onInspect(`batch:${session.id}`)}
            >
              <kbd className="ak">D</kbd>
              <span>{session.rows.length} one by one</span>
            </button>
          ) : undefined
        }
      />
      {current.length ? <ul className="lk-arows">{current.map(row)}</ul> : null}
      {outdated.length ? (
        <details className="lk-outdated">
          <summary>
            {outdated.length} may be outdated
            <span className="lk-muted"> · their run or machine has moved on</span>
          </summary>
          <ul className="lk-arows">{outdated.map(row)}</ul>
        </details>
      ) : null}
      {assumptions.length ? (
        <ul className="lk-assumed" aria-label="Assumptions">
          {assumptions.map((d) => (
            <li key={d.id}>
              <span className="vk-icol">
                <Sparkles size={16} aria-hidden="true" />
              </span>
              <span className="lk-assumed__t">
                Assumed {d.resolution?.label ?? 'an option'}
                <span className="lk-assumed__m">{d.resolution?.assumption}</span>
              </span>
              <button
                type="button"
                className="lk-link"
                onClick={() => onInspect(`decision:${d.id}`)}
              >
                Change
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {outside}
    </section>
  )
}

/** What waits on an ask, in words ("2 tasks wait", "the run waits"), never a chip. */
export function askConsequence(r: InboxRow): string | undefined {
  if (r.blocking && isBlockedRow(r)) return blockedReason(r)
  if (r.unblocks > 1) return `${r.unblocks} tasks wait`
  if (r.unblocks === 1) return '1 task waits'
  if (r.reasons.includes('Blocks a run')) return 'the run waits'
  const kind = r.reasons.find((x) => !/^(Blocks|Unblocks)/.test(x))
  return kind ? kind.toLowerCase() : undefined
}

/**
 * One Needs-you row in a project (app-clarity `project`): status icon, the question, then one
 * meta line (lane · what waits on it), the age on the right. Answers live in the inspector; the
 * top ask still takes `1`–`9`.
 */
function ProjectAskRow({
  row,
  focused,
  current,
  onOpen,
}: {
  row: InboxRow
  focused: boolean
  current: boolean
  onOpen: () => void
}) {
  const acts = useRowActions(inboxRowEntity(row), { skipKeys: TRIAGE_KEYS })
  const blocked = row.blocking && isBlockedRow(row)
  const meta = [row.lane ?? row.projectName, askConsequence(row)].filter(Boolean).join(' · ')
  const title = row.locked ? 'Encrypted: unlock this browser to read' : row.title
  return (
    <li
      className={`lk-arow${focused ? ' is-focused' : ''}${current ? ' is-current' : ''}${blocked ? ' is-blocked' : row.blocking ? ' is-needs' : ''}`}
      data-row-key={row.key}
      data-kind={row.kind}
      {...acts.rowProps}
    >
      <button
        type="button"
        className="lk-arow__main"
        data-row-main
        aria-current={current ? 'true' : undefined}
        aria-label={`${title}. ${blocked ? 'Blocked' : 'Needs you'}${meta ? `, ${meta}` : ''}`}
        onClick={onOpen}
      >
        <span className={`vk-icol${row.blocking ? '' : ' is-soft'}`}>
          <StatusIcon kind={blocked ? 'blocked' : 'wait'} />
        </span>
        <span className="lk-arow__t">{title}</span>
        <span className="lk-arow__age">
          <Ago ts={row.createdAt} />
        </span>
        {meta ? (
          <span className="lk-arow__m">
            {blocked ? (
              <>
                <b>Blocked</b> · {meta}
              </>
            ) : (
              meta
            )}
          </span>
        ) : null}
        {row.note ? <span className="lk-arow__m">{row.note}</span> : null}
      </button>
      <RowActionBar api={acts} label={row.title} />
    </li>
  )
}

/** The Lanes inspector key for a row (hub ids carry colons: encoded, decoded by the inspector). */
function keyOf(r: InboxRow): string {
  return r.view?.source === 'entity' || !r.item ? r.key : `ask:${encodeURIComponent(r.item.id)}`
}

provideLanesSlots({ DecisionInspector: LanesDecision, AsksPanel: ProjectAsks })
